import { useState, useEffect } from "react";
import {
  useGetGeneralLedgerSummary,
  useGetGeneralLedgerTransactions,
  useGetAllChartOfAccounts,
} from "../../../../hooks/api/queries";
import { getUrlQuerysection, monthStartAndEndDate } from "../../../../helpers";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

const useGeneralLedger = () => {
  const [search, setSearch] = useSearchParams();
  const [selectedChartAccount, setSelectedChartAccount] = useState(
    search.get("chart_account_id"),
  );
  const { startOfMonth, endOfMonth } = monthStartAndEndDate();
  const [currentStore, setCurrentStore] = useState(search.get("store_id"));
  const { storeList } = useSelector((state) => state.profileDetailsReducer);
  const [entrySource, setEntrySource] = useState(search.get("entry_source"));
  const [showExportAccountStatement, setShowExportAccountStatement] =
    useState(false);
  if (!search.get("start_date")) {
    search.set("start_date", startOfMonth);
    setSearch(search, { replace: true });
  }
  if (!search.get("end_date")) {
    search.set("end_date", endOfMonth);
    setSearch(search, { replace: true });
  }
  const [startDate] = useState(search.get("start_date"));
  const [endDate] = useState(search.get("end_date"));
  const queries = getUrlQuerysection();
  const summarySearchParams = new URLSearchParams(queries);
  summarySearchParams.delete("page");
  summarySearchParams.delete("per_page");
  const summaryQueries = `?${summarySearchParams.toString()}`;
  const canFetchChartAccountTransactions =
    Boolean(startDate) && Boolean(endDate) && Boolean(selectedChartAccount);

  const {
    isLoading: isLoadingChartAccountTransactions,
    data: chartAccountTransactions,
    refetch: handleFetchChartAccountTransactions,
  } = useGetGeneralLedgerTransactions({
    queries,
    enabled: canFetchChartAccountTransactions,
  });

  const {
    isLoading: isLoadingChartAcountsList,
    data: chartAccountsList,
    refetch: handleChartAccountsList,
  } = useGetAllChartOfAccounts();

  const {
    isLoading: isLoadingGeneralLedgerSummary,
    data: generalLedgerSummary,
  } = useGetGeneralLedgerSummary({
    queries: summaryQueries,
    enabled: canFetchChartAccountTransactions,
  });

  useEffect(() => {
    if (chartAccountsList?.data?.length > 0 && !selectedChartAccount) {
      filterByChartAccount(chartAccountsList?.data?.[0]?.id);
    }
  });

  const filterByChartAccount = (value) => {
    search.set("chart_account_id", value);
    setSelectedChartAccount(value);
    setSearch(search, { replace: true });
  };

  const gotoPage = (pageNumber) => {
    search.set("page", pageNumber);
    setSearch(search, { replace: true });
  };

  const setDateFilter = (data) => {
    search.set("start_date", data.startDate);
    search.set("end_date", data.endDate);
    setSearch(search, { replace: true });
  };

  const filterByStoreId = (storeId) => {
    if (storeId === "ALL_STORES") search.delete("store_id");
    else search.set("store_id", storeId);
    setCurrentStore(storeId);
    setSearch(search, { replace: true });
  };

  const setEntrySourceFilter = (source) => {
    if (!source) search.delete("entry_source");
    else search.set("entry_source", source);
    setEntrySource(source);
    setSearch(search, { replace: true });
  };

  return {
    storeList,
    chartAccountTransactions: chartAccountTransactions?.data || [],
    metaDetails: chartAccountTransactions?.meta,
    isLoadingChartAccountTransactions,
    generalLedgerSummary: generalLedgerSummary?.data || {},
    isLoadingGeneralLedgerSummary,
    chartAccountsList: chartAccountsList?.data || [],
    isLoadingChartAcountsList,
    setSelectedChartAccount,
    handleChartAccountsList,
    filterByChartAccount,
    selectedChartAccount,
    gotoPage,
    startDate,
    endDate,
    currentStore,
    entrySource,
    showExportAccountStatement,
    setDateFilter,
    handleFetchChartAccountTransactions,
    filterByStoreId,
    setEntrySourceFilter,
    setShowExportAccountStatement,
  };
};

export default useGeneralLedger;
